import { useSeason } from '../../context/SeasonContext';
import { useCallback, useEffect, useState } from 'react';
import styles from './myTeam.module.css';
import plus from '../../assets/icons/plus.svg';
import closeIcon from '../../assets/icons/close.svg';
import CardTeam from '../../components/CardTeam';
import TeamModal from '../../components/TeamModal';
import { ModalPortal } from '../../components/ModalPortal/ModalPortal';
import { createTeam, getMyTeams, getTeamMembers } from '../../api/teams';
import avatarPlaceholder from '../../assets/icons/avatar.svg';
import { useAuth } from '../../context/AuthContext';
import { isAdminLikeRole } from '../../utils/roles';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

export const MyTeam = () => {
  const { user } = useAuth();
  const { canEdit } = useSeason();
  const isCreateTeamEnabled = canEdit && isAdminLikeRole(user?.role);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTeamDetailsOpen, setIsTeamDetailsOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobile();

  const loadTeams = useCallback(async () => {
    try {
      setError('');
      const myTeams = await getMyTeams();
      const cards = await Promise.all(
        myTeams.map(async (team) => {
          const members = await getTeamMembers(team.team_id);
          return {
            id: team.team_id,
            teamName: team.name,
            balance: team.balance,
            systemRole: team.system_role,
            members: members.map((member) => ({
              name: member.login,
              avatar: member.avatar_url || avatarPlaceholder
            }))
          };
        })
      );
      setTeams(cards);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const handleCreateTeam = () => {
    if (!isCreateTeamEnabled) {
      setError('Создание команд временно недоступно. Функция появится позже.');
      return;
    }
    setIsModalOpen(true);
    setTeamName('');
    setFieldError('');
  };

  const handleOpenTeamDetails = (team) => {
    setSelectedTeam(team);
    setIsTeamDetailsOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTeamName('');
    setFieldError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!teamName.trim()) {
      setFieldError('Введите название команды');
      return;
    }
    if (teamName.trim().length > 100) {
      setFieldError('Название команды не должно превышать 100 символов');
      return;
    }

    try {
      setIsSubmitting(true);
      await createTeam(teamName.trim());
      await loadTeams();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
      handleCloseModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.title}>Мои команды</h1>
        <button
          className={`${styles.createTeam} ${!isCreateTeamEnabled ? styles.createTeamDisabled : ''}`}
          onClick={handleCreateTeam}
          disabled={!isCreateTeamEnabled}
          title={!isCreateTeamEnabled ? 'Создание команд будет доступно позже' : 'Создать команду'}
        >
          <img src={plus} alt="+" />
          {isCreateTeamEnabled ? 'Создать команду' : 'Создание скоро'}
        </button>
      </div>

      {error && <p>{error}</p>}
      {loading ? null : (
        <div className={styles.teamsList}>
          {teams.map((team) => (
            <CardTeam
              key={team.id}
              teamName={team.teamName}
              balance={team.balance}
              members={team.members}
              onButtonClick={() => handleOpenTeamDetails(team)}
            />
          ))}
        </div>
      )}

      {isModalOpen && (
        <ModalPortal onClose={handleCloseModal} isMobile={isMobile}>
          <div className={styles.modalContentWrapper}>
            <button className={styles.modalClose} onClick={handleCloseModal}>
              <img src={closeIcon} alt="Закрыть" />
            </button>
            <h2 className={styles.modalTitle}>Создание команды</h2>
            <form className={styles.modalForm} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="team-name">
                  Введите название команды
                </label>
                <input
                  className={styles.formInput}
                  id="team-name"
                  type="text"
                  value={teamName}
                  maxLength={100}
                  onChange={(e) => {
                    setTeamName(e.target.value);
                    setFieldError('');
                  }}
                  placeholder="Введите название команды"
                  autoFocus
                />
                {fieldError && <p className={styles.formError}>{fieldError}</p>}
              </div>
              <button
                className={styles.modalButton}
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Создаем...' : 'Создать'}
              </button>
            </form>
          </div>
        </ModalPortal>
      )}

      <TeamModal
        team={selectedTeam}
        isOpen={isTeamDetailsOpen}
        onClose={() => setIsTeamDetailsOpen(false)}
        onTeamChanged={loadTeams}
      />
    </div>
  );
};
